/**
 * SHELL — the one-shot run flow: countdown -> live HUD -> finish.
 *
 * This screen owns the run-time chrome only. It creates the InputController on
 * the Render surface, starts the fixed-timestep loop, shows a minimal HUD (time,
 * checkpoints) plus a first-drag steer hint, and on finish calls app.finishRun
 * with the recorded replay. The actual physics/visuals belong to Core/Render.
 */

import { MAX_RUN_TICKS } from '@/types';
import type { GameState } from '@/types';
import type { App } from '../../app';
import type { Screen } from '../router';
import { div, el } from '../dom';
import { formatTime } from '../format';
import { createInputController } from '../../game/input';
import type { InputController } from '../../game/input';
import { startRunLoop } from '../../game/loop';
import type { LoopHandle } from '../../game/loop';

export function runScreen(app: App, opts: { practice: boolean }): Screen {
  const root = div('');
  const totalCheckpoints = app.course.checkpoints.length;

  // --- HUD -----------------------------------------------------------------
  const timeEl = el('span', { class: 'dr-hud__time', text: '0:00.00' });
  const cpEl = el('span', { text: `0/${totalCheckpoints}` });
  const hud = div('dr-hud');
  hud.append(
    div('dr-hud__chip', el('small', { text: 'Time' }), timeEl),
    div('dr-hud__chip', el('small', { text: 'CP' }), cpEl),
  );
  if (opts.practice) {
    hud.append(div('dr-hud__chip', el('small', { text: 'Mode' }), el('span', { text: 'Practice' })));
  }

  const steerHint = div('dr-steerhint');
  steerHint.textContent = 'Drag anywhere to steer';

  const countdown = div('dr-steerhint');
  countdown.style.bottom = 'auto';
  countdown.style.top = '50%';
  countdown.style.fontSize = '64px';
  countdown.style.fontWeight = '800';

  root.append(hud, steerHint);

  let input: InputController | null = null;
  let loop: LoopHandle | null = null;
  let disposed = false;

  const onTick = (state: GameState): void => {
    timeEl.textContent = formatTime(state.elapsedMs);
    cpEl.textContent = `${state.checkpointIndex}/${totalCheckpoints}`;
  };

  const onFinish = (state: GameState): void => {
    input?.dispose();
    const replay = app.sessionReplay();
    void app.finishRun(state, replay, opts.practice);
  };

  // --- Countdown then go ---------------------------------------------------
  const beginCountdown = async (): Promise<void> => {
    root.appendChild(countdown);
    for (const label of ['3', '2', '1', 'GO']) {
      if (disposed) return;
      countdown.textContent = label;
      countdown.style.opacity = '1';
      await wait(label === 'GO' ? 450 : 700);
      countdown.style.opacity = '0';
      await wait(120);
    }
    countdown.remove();
    if (disposed) return;
    start();
  };

  const start = (): void => {
    const sim = app.activeSim();
    input = createInputController(app.surface, {
      onFirstInput: () => {
        steerHint.style.opacity = '0';
      },
    });
    loop = startRunLoop({
      sim,
      renderer: app.renderer,
      input,
      onTick,
      onFinish,
      // Safety: in case status never flips, the cap finishes it.
      maxCatchUpTicks: 5,
    });
    void MAX_RUN_TICKS; // documented cap, enforced inside the loop
  };

  void beginCountdown();

  return {
    node: root,
    dispose() {
      disposed = true;
      loop?.stop();
      input?.dispose();
    },
  };
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
